import { useEffect, useMemo } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { useMount } from "app/custom-hooks/useMount";
import { ToastMessage } from "app/pages/activation/components/HomeWithData/Alerts/ToastMessage/ToastMessage";
import { useHomeContext } from "app/pages/activation/context/Home/HomeContext";
import { getDurationForPriorityAlertDate } from "app/pages/activation/helpers/home/alerts/getDurationForPriorityAlertDate";
import { useContainerVisibility } from "app/pages/activation/hooks/helpers/useContainerVisibility";
import { useToasts } from "app/pages/activation/hooks/helpers/useToasts";
import { useActivationMetadataQuery } from "app/pages/activation/queries/data/useActivationMetadataQuery";
import {
    PRIORITY_ALERTS_KEY,
    usePriorityAlertsQuery,
} from "app/pages/activation/queries/homeWithData/usePriorityAlertsQuery";
import { activationTracking } from "app/pages/activation/services/tracking";
import { PriorityAlertsStatus, type PriorityAlertItem } from "app/pages/activation/types/api";
import { useAlertsPageNavigation } from "app/pages/activation-alerts/hooks/useAlertsPageNavigation";

import { WORLDWIDE } from "../constants";

import { getPriorityAlertNavigationExtras } from "./priorityAlertNavigation";

export const useAlerts = () => {
    const { navigateToAlerts } = useAlertsPageNavigation();
    const { showSuccessToast } = useToasts();
    const { totalNumber, activeActivationId, projects } = useHomeContext();

    const { isLoading: isMetadataLoading } = useActivationMetadataQuery();
    const {
        data: alertsData,
        isLoading: isAlertsLoading,
        isFetching: isAlertsFetching,
    } = usePriorityAlertsQuery(activeActivationId);

    const queryClient = useQueryClient();
    const track = activationTracking.homePageWithData;

    const isLoading = isAlertsLoading || isMetadataLoading || isAlertsFetching;
    const alertsLength = alertsData?.priorityAlerts?.length;

    const hasProjects = !!projects?.length && !!activeActivationId;
    const hasNewAlerts = alertsData?.status === PriorityAlertsStatus.Success && !isLoading && !!activeActivationId;
    const isAllRead = alertsData?.status !== PriorityAlertsStatus.Success && !isLoading && hasProjects;
    const shouldShowViewBtn = !isLoading && hasProjects;

    useEffect(() => {
        if (activeActivationId) {
            return;
        }

        void queryClient.removeQueries({ queryKey: [PRIORITY_ALERTS_KEY] });
    }, [activeActivationId, queryClient]);

    useMount(() => void queryClient.invalidateQueries({ queryKey: [PRIORITY_ALERTS_KEY] }));

    const currentProject = useMemo(
        () => projects?.find((project) => project.id === activeActivationId) ?? projects?.[0],
        [activeActivationId, projects],
    );

    const goToAlerts = (alert?: PriorityAlertItem) => {
        const key = activeActivationId || currentProject.id;

        if (alert) {
            const { country, tab, alertsType } = getPriorityAlertNavigationExtras(alert);

            void navigateToAlerts({
                key,
                country,
                tab,
                alertsType,
                duration: getDurationForPriorityAlertDate(alert.date),
            });
            return;
        }

        void navigateToAlerts({
            key,
            country: currentProject?.country ?? WORLDWIDE,
        });
    };

    const onViewAlerts = () => {
        track.trackViewAllAlerts();
        goToAlerts();
    };

    const containerRef = useContainerVisibility({
        enabled: !isLoading && !!alertsLength,
        onNotVisible: () => {
            showSuccessToast(
                <ToastMessage
                    unreadCount={alertsLength}
                    onClick={() => {
                        containerRef.current?.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });
                    }}
                />,
            );
        },
    });

    return {
        isLoading,
        hasProjects,
        hasNewAlerts,
        isAllRead,
        shouldShowViewBtn,
        totalNumber,
        alerts: alertsData?.priorityAlerts,
        onViewAlerts,
        goToAlerts,
        containerRef,
    };
};
